import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Gift, ShoppingCart } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { firstAvailable, variantAvailable, cartLabel } from "@/lib/merchStock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function OptionPicker({ p, sel, onChange, testId }) {
  if (!p.sizes?.length && !p.colors?.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {p.sizes?.length > 0 && (
        <select data-testid={`${testId}-size`} value={sel.size} onChange={(e) => onChange({ ...sel, size: e.target.value })} className="h-7 rounded border border-zinc-300 bg-white px-1.5 text-[11px] font-bold">
          {p.sizes.map((s) => <option key={s} value={s} disabled={variantAvailable(p, s, sel.color) <= 0}>{s}{variantAvailable(p, s, sel.color) <= 0 ? " (sold out)" : ""}</option>)}
        </select>
      )}
      {p.colors?.length > 0 && (
        <select data-testid={`${testId}-color`} value={sel.color} onChange={(e) => onChange({ ...sel, color: e.target.value })} className="h-7 rounded border border-zinc-300 bg-white px-1.5 text-[11px] font-bold">
          {p.colors.map((c) => <option key={c} value={c} disabled={variantAvailable(p, sel.size, c) <= 0}>{c}</option>)}
        </select>
      )}
    </div>
  );
}

function BundleCard({ b, idx }) {
  const { addItem, setOpen } = useCart();
  const [sel, setSel] = useState(() => Object.fromEntries(b.products.map((p) => [p.id, firstAvailable(p)])));
  const factor = b.bundle_price / (b.regular_total || 1);
  const soldOut = b.products.some((p) => variantAvailable(p, sel[p.id]?.size, sel[p.id]?.color) <= 0);

  const add = () => {
    if (soldOut) return toast.error("One of the bundle items is sold out in that option");
    b.products.forEach((p) => {
      const s = sel[p.id] || {};
      addItem(p, cartLabel(s.size, s.color), { bundle_id: b.id, bundle_title: b.title, bundle_product_ids: b.product_ids, bundle_factor: factor });
    });
    toast.success(`${b.title} added · you save $${b.savings.toFixed(2)}`);
    setOpen(true);
  };

  return (
    <motion.article
      id={`bundle-${b.id}`}
      data-testid={`bundle-card-${idx + 1}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay: idx * 0.08 }}
      className="rounded-2xl border-2 border-[#0C2E2B] bg-white overflow-hidden shadow-[6px_6px_0_#0C2E2B] scroll-mt-40 target:ring-4 target:ring-[#FFDF00]"
    >
      <div className="flex items-stretch">
        {b.products.map((p, i) => (
          <div key={p.id} className="relative flex-1 min-w-0">
            <img src={p.image} alt={p.title} className="w-full h-40 sm:h-48 object-cover" />
            {i < b.products.length - 1 && (
              <span className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-[#FFDF00] border-2 border-[#0C2E2B] grid place-items-center"><Plus className="w-4 h-4 text-[#0C2E2B]" /></span>
            )}
          </div>
        ))}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <Badge className="bg-red-600 hover:bg-red-600 text-white text-[10px] font-bold mb-1"><Gift className="w-3 h-3 mr-1" /> Bundle · save ${b.savings.toFixed(2)}</Badge>
            <h3 className="font-display font-black text-xl text-[#0C2E2B]" data-testid={`bundle-title-${idx + 1}`}>{b.title}</h3>
            {b.blurb && <p className="text-xs text-zinc-600 mt-0.5">{b.blurb}</p>}
          </div>
          <div className="text-right shrink-0">
            <div className="font-mono-data font-black text-2xl text-red-600" data-testid={`bundle-price-${idx + 1}`}>${b.bundle_price.toFixed(2)}</div>
            <s className="text-sm text-zinc-400 font-bold">${b.regular_total.toFixed(2)}</s>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {b.products.map((p) => (
            <div key={p.id} className="rounded-md bg-zinc-50 border border-zinc-200 p-2">
              <div className="text-xs font-bold text-zinc-800 truncate">{p.title}</div>
              <OptionPicker p={p} sel={sel[p.id] || {}} onChange={(v) => setSel({ ...sel, [p.id]: v })} testId={`bundle-${idx + 1}-${p.id}`} />
            </div>
          ))}
        </div>
        <Button data-testid={`bundle-add-btn-${idx + 1}`} onClick={add} disabled={soldOut} className="btn-kinetic mt-4 w-full h-11 rounded-md bg-[#0C2E2B] text-[#FFDF00] hover:bg-[#0C2E2B]/90 font-black disabled:opacity-50">
          <ShoppingCart className="w-4 h-4 mr-2" /> {soldOut ? "Sold out in that option" : `Add bundle · $${b.bundle_price.toFixed(2)}`}
        </Button>
      </div>
    </motion.article>
  );
}

export function useBundles() {
  const [bundles, setBundles] = useState([]);
  useEffect(() => { axios.get(`${API}/merch/bundles`).then(({ data }) => setBundles(data.items || [])).catch(() => {}); }, []);
  return bundles;
}

export default function BundleDeals({ bundles: given }) {
  const fetched = useBundles();
  const bundles = given ?? fetched;
  if (!bundles.length) return null;
  return (
    <section className="pt-10" data-testid="bundle-deals">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="font-tactical text-[10px] uppercase tracking-[0.3em] text-[#0C2E2B]/60">Bundle deals</div>
          <h2 className="font-display font-black text-2xl text-[#0C2E2B]">Pair up &amp; save</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {bundles.map((b, i) => <BundleCard key={b.id} b={b} idx={i} />)}
      </div>
    </section>
  );
}
